import type { BusStop } from '../../domain/types'
import type { Locale } from '@/intl/types'
import { Icon } from '@sunwebgroup/uds-ui/icon'
import { Typography } from '@sunwebgroup/uds-ui/typography'
import { useIntl } from 'react-intl'
import { formatDate, formatPeriod, formatTime } from '@/scopes/account/common/formatters'
import styles from './BusDetailsCard.module.css'
import { IconRow } from './IconRow'

interface BusDetailsCardProps {
  stop: BusStop | null
  showPickupTime?: boolean
  noBusTitle: string
  noBusDescription: string
}

export function BusDetailsCard({ stop, showPickupTime = false, noBusTitle, noBusDescription }: BusDetailsCardProps) {
  const { formatMessage, locale } = useIntl()
  const currentLocale = locale as Locale

  if (!stop) {
    return (
      <div className={styles.emptyCard}>
        <Icon glyph="BusFront" size={32} className={styles.emptyIcon} />
        <div>
          <Typography as="h3" variant="display-i-regular">
            {noBusTitle}
          </Typography>
          <Typography as="p" variant="body-s-regular-loose" className={styles.emptyDescription}>
            {noBusDescription}
          </Typography>
        </div>
      </div>
    )
  }

  return (
    <div className={styles.cardBase}>
      <div className={styles.header}>
        <Typography as="h3" variant="display-i-regular">
          {stop.city}
        </Typography>
        <Typography as="p" variant="body-s-regular-tight" className={styles.date}>
          {stop.arrivalDate
            ? formatPeriod(stop.departureDate, stop.arrivalDate, currentLocale)
            : formatDate(stop.departureDate, currentLocale)}
        </Typography>
      </div>
      <div className={styles.content}>
        <div className={styles.row}>
          <div className={styles.field}>
            <IconRow glyph="MapPin" description={formatMessage({ id: 'booking_details_transfers_pickup_location' })} />
            <Typography as="p" variant="body-m-bold-tight" className={styles.fieldValue}>
              {stop.location}
            </Typography>
          </div>
          {showPickupTime && stop.pickupTime && (
            <div className={styles.fieldRight}>
              <IconRow glyph="Clock" description={formatMessage({ id: 'booking_details_transfers_pickup_time' })} />
              <Typography as="p" variant="body-m-bold-tight" className={styles.fieldValue}>
                {formatTime(stop.pickupTime ?? undefined)}
              </Typography>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
